/**
 * To-do lists domain API (Arbeitsorganisation, Migration 162).
 *
 * Targets the org-scoped `/api/admin/todo-lists` endpoints. Mirrors the
 * `tasks.js` style: thin fetch wrappers that parse the admin error
 * envelope. Response shapes match the backend Pydantic schemas in
 * `app/schemas/todo_list.py`.
 *
 * Endpoints:
 *   GET    /api/admin/todo-lists                        { items, total }
 *   POST   /api/admin/todo-lists                        TodoListRead (201)
 *   GET    /api/admin/todo-lists/{id}                   TodoListDetailRead
 *   PATCH  /api/admin/todo-lists/{id}                   TodoListRead
 *   DELETE /api/admin/todo-lists/{id}                   204 No Content
 *   POST   /api/admin/todo-lists/{id}/items             TodoItemRead (201)
 *   PATCH  /api/admin/todo-lists/{id}/items/{itemId}    TodoItemRead
 *   DELETE /api/admin/todo-lists/{id}/items/{itemId}    204 No Content
 *   POST   .../items/{itemId}/task                      TodoItemRead (201)
 *   GET    /api/admin/todo-lists/{id}/activity          { items, total }
 *
 * **Free text is the default.** `createTodoItem(listId, { title })` is a
 * complete call — no template, no entity link, no task, no due date and
 * no assignee. Everything else in the payload is optional, and the
 * backend regression tests pin that.
 *
 * Entity links follow the shared ME1 contract (the same one tasks and
 * work reports use): write `<type>_ids: string[]`, read `<plural>` as
 * `[{ link_id, id, label, is_manual, derived_from }]`. Omitting an
 * `*_ids` field leaves that type untouched; `[]` clears it.
 */
import {
  API_BASE_URL,
  getApiHeaders,
  adminErrorFrom,
} from "./core";

function _qs(params) {
  const sp = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null || value === "") return;
    sp.append(key, String(value));
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

async function _json(res) {
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

async function _noContent(res) {
  if (!res.ok) throw await adminErrorFrom(res);
  return true;
}

/**
 * GET /api/admin/todo-lists
 *
 * Each row carries the derived progress counters (`total_items`,
 * `completed_items`, `progress_percent`) so a card needs no second
 * request. `completed_items` counts EFFECTIVE completion — an item is
 * done when it is ticked or when its linked task is `done`.
 *
 * @param {{ q?: string, skip?: number, limit?: number }} [params]
 * @returns {Promise<{ items: Array<object>, total: number }>}
 */
export function fetchTodoLists(params = {}) {
  return fetch(`${API_BASE_URL}/api/admin/todo-lists${_qs(params)}`, {
    headers: getApiHeaders(),
  }).then(_json);
}

/**
 * GET /api/admin/todo-lists/{listId} → list plus its `items[]`.
 * One request fills the whole right-hand pane.
 */
export function fetchTodoList(listId) {
  return fetch(
    `${API_BASE_URL}/api/admin/todo-lists/${encodeURIComponent(listId)}`,
    { headers: getApiHeaders() }
  ).then(_json);
}

/**
 * POST /api/admin/todo-lists
 * @param {{ title: string, description?: string, priority?: string,
 *           due_date?: string, assigned_to_user_id?: string }} payload
 */
export function createTodoList(payload) {
  return fetch(`${API_BASE_URL}/api/admin/todo-lists`, {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify(payload),
  }).then(_json);
}

/** PATCH /api/admin/todo-lists/{listId} — omitted fields stay unchanged. */
export function updateTodoList(listId, payload) {
  return fetch(
    `${API_BASE_URL}/api/admin/todo-lists/${encodeURIComponent(listId)}`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  ).then(_json);
}

/** DELETE /api/admin/todo-lists/{listId} — items and links cascade. */
export function deleteTodoList(listId) {
  return fetch(
    `${API_BASE_URL}/api/admin/todo-lists/${encodeURIComponent(listId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  ).then(_noContent);
}

/**
 * POST /api/admin/todo-lists/{listId}/items
 *
 * `{ title: "Fenster im Zimmer prüfen und Fotos machen" }` is valid on
 * its own — that is the product contract, not a convenience.
 *
 * @param {string} listId
 * @param {{ title: string, description?: string, priority?: string,
 *           due_at?: string, assigned_to_user_id?: string,
 *           task_id?: string, tenant_ids?: string[], tenancy_ids?: string[],
 *           unit_ids?: string[], application_ids?: string[],
 *           landlord_ids?: string[] }} payload
 */
export function createTodoItem(listId, payload) {
  return fetch(
    `${API_BASE_URL}/api/admin/todo-lists/${encodeURIComponent(listId)}/items`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  ).then(_json);
}

/**
 * PATCH /api/admin/todo-lists/{listId}/items/{itemId}
 *
 * Ticking is `{ completed: true }` / `{ completed: false }`. It stamps or
 * clears the item's own completion pair and never mutates a linked task
 * — task status transitions belong to the task service (one-way
 * integration).
 */
export function updateTodoItem(listId, itemId, payload) {
  return fetch(
    `${API_BASE_URL}/api/admin/todo-lists/${encodeURIComponent(
      listId
    )}/items/${encodeURIComponent(itemId)}`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  ).then(_json);
}

/** DELETE /api/admin/todo-lists/{listId}/items/{itemId} */
export function deleteTodoItem(listId, itemId) {
  return fetch(
    `${API_BASE_URL}/api/admin/todo-lists/${encodeURIComponent(
      listId
    )}/items/${encodeURIComponent(itemId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  ).then(_noContent);
}

/**
 * POST .../items/{itemId}/task — promote a to-do into a real task.
 *
 * Title, description, due date, priority and assignee come from the
 * to-do, so the two cannot start out disagreeing. 409 when the item
 * already has a task.
 */
export function createTaskFromTodoItem(listId, itemId, payload = {}) {
  return fetch(
    `${API_BASE_URL}/api/admin/todo-lists/${encodeURIComponent(
      listId
    )}/items/${encodeURIComponent(itemId)}/task`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify({ task_type: "general", ...payload }),
    }
  ).then(_json);
}

/** GET /api/admin/todo-lists/{listId}/activity — list + item audit feed. */
export function fetchTodoListActivity(listId) {
  return fetch(
    `${API_BASE_URL}/api/admin/todo-lists/${encodeURIComponent(
      listId
    )}/activity`,
    { headers: getApiHeaders() }
  ).then(_json);
}
